import { useCallback } from "react";

import {
    AnalyticsEventPayload,
    useAnalyticsEvents
} from "@atlaskit/analytics-next";
import FeatureGates from "@atlassian/feature-gate-js-client";
import { useFireCrossFlowAnalyticsEvents } from "@atlassian/surface-analytics";
import {
    CrossFlowInteractedUIEvents,
    CrossFlowViewedUIEvents
} from "@atlassian/surface-analytics/dist/types/cross-flow/types";
import {
    CrossFlowViewedScreenNames,
    InteractedCommonAttributes,
    ViewedCommonAttributes,
    ViewedUICommonAttributes
} from "@atlassian/surface-analytics-specs";
import { Attributes } from "@atlassian/wac-analytics-web-client-types";
import { ProductKeys } from "@atlassiansox/asc-stargate";

import { useAnalyticsClient } from "../../../../analytics/wac-analytics/analytics-client";

export const productRolloutExperimentMap: Partial<Record<ProductKeys, string>> =
    {
        [ProductKeys.CONFLUENCE]:
            "cross-flow_frontend_for_confluence_product_tour",
        [ProductKeys.JIRA_SOFTWARE]: "cross-flow_frontend_for_jira",
        [ProductKeys.JIRA_PRODUCT_DISCOVERY]:
            "cross-flow_frontend_for_jpd_product_tour",
        [ProductKeys.JIRA_SERVICE_DESK]: "streamline_cross-flow_from_wac"
    };

export const getRolloutFlagValue = (
    productKey?: ProductKeys,
    fireExperimentExposure?: boolean
) => {
    if (!productKey) {
        return "not-enrolled";
    }
    const experimentKey = productRolloutExperimentMap[productKey];
    if (!experimentKey) {
        return "not-enrolled";
    }
    return FeatureGates.getExperimentValue(
        experimentKey,
        "cohort",
        "not-enrolled",
        {
            fireExperimentExposure
        }
    );
};

export const getProductCohorts = (productKeys: ProductKeys[]) => {
    const productCohorts: {
        [key: string]: string;
    } = {};
    productKeys.forEach((productKey) => {
        productCohorts[productKey] = getRolloutFlagValue(productKey);
    });
    return productCohorts;
};

export const useFireEvent = (initialAttributes: Attributes = {}) => {
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const crossFlowEvents = useFireCrossFlowAnalyticsEvents();

    const fireEvent = useCallback(
        (eventPayload: AnalyticsEventPayload, attributes: Attributes = {}) => {
            analyticsClient &&
                createAnalyticsEvent({
                    ...eventPayload,
                    attributes: {
                        ...(eventPayload["attributes"] || {}),
                        ...attributes,
                        ...initialAttributes
                    }
                }).fire();
        },
        [analyticsClient, createAnalyticsEvent, initialAttributes]
    );
    const fireUIEvent = useCallback(
        (eventPayload: AnalyticsEventPayload, attributes: Attributes = {}) => {
            fireEvent({
                ...eventPayload,
                attributes: {
                    ...attributes,
                    ...eventPayload["attributes"]
                },
                eventType: "ui"
            });
        },
        [fireEvent]
    );
    const fireTrackEvent = useCallback(
        (eventPayload: AnalyticsEventPayload, attributes: Attributes = {}) => {
            fireEvent({
                ...eventPayload,
                attributes: {
                    ...attributes,
                    ...eventPayload["attributes"]
                },
                eventType: "track"
            });
        },
        [fireEvent]
    );
    const fireScreenEvent = useCallback(
        (eventPayload: AnalyticsEventPayload, attributes: Attributes = {}) => {
            fireEvent({
                ...eventPayload,
                attributes: {
                    ...attributes,
                    ...eventPayload["attributes"]
                },
                eventType: "screen"
            });
        },
        [fireEvent]
    );
    const fireOperationalEvent = useCallback(
        (eventPayload: AnalyticsEventPayload, attributes: Attributes = {}) => {
            fireEvent({
                ...eventPayload,
                attributes: {
                    ...attributes,
                    ...eventPayload["attributes"]
                },
                eventType: "operational"
            });
        },
        [fireEvent]
    );

    const fireCrossFlowScreenEvent = useCallback(
        (
            screenName: CrossFlowViewedScreenNames,
            commonAttributes: ViewedCommonAttributes,
            attributes: Attributes = {}
        ) => {
            if (!analyticsClient) {
                return;
            }
            crossFlowEvents.fireViewedScreenEvent(
                screenName,
                commonAttributes,
                {
                    ...initialAttributes,
                    ...attributes
                }
            );
        },
        [analyticsClient, crossFlowEvents, initialAttributes]
    );

    const fireCrossFlowViewedUIEvent = useCallback(
        (
            eventPayload: CrossFlowViewedUIEvents,
            commonAttributes: ViewedUICommonAttributes,
            attributes: Attributes = {}
        ) => {
            if (!analyticsClient) {
                return;
            }
            crossFlowEvents.fireViewedUIEvent(eventPayload, commonAttributes, {
                ...initialAttributes,
                ...attributes
            });
        },
        [analyticsClient, crossFlowEvents, initialAttributes]
    );

    const fireCrossFlowInteractedUIEvent = useCallback(
        (
            eventPayload: CrossFlowInteractedUIEvents,
            commonAttributes: InteractedCommonAttributes,
            attributes: Attributes = {}
        ) => {
            if (!analyticsClient) {
                return;
            }
            crossFlowEvents.fireInteractedUIEvent(
                eventPayload,
                commonAttributes,
                {
                    ...initialAttributes,
                    ...attributes
                }
            );
        },
        [analyticsClient, crossFlowEvents, initialAttributes]
    );

    return {
        fireUIEvent,
        fireTrackEvent,
        fireScreenEvent,
        fireOperationalEvent,
        fireCrossFlowScreenEvent,
        fireCrossFlowViewedUIEvent,
        fireCrossFlowInteractedUIEvent
    };
};

export const WAC_GENERIC_TOUCHPOINT_NAME = "wacGenericCrossFlowTouchpoint";
export const WAC_GENERIC_TOUCHPOINT_COMPONENT_NAME =
    "wacGenericCrossFlowComponent";

export const redirectToDefaultUrl = (
    redirectUrl: string,
    onSuccess: () => void,
    onError: (message: string) => void
) => {
    if (typeof redirectUrl !== "string" || redirectUrl === "") {
        onError(`Fallback href ${redirectUrl} is not a string or empty`);
        return;
    }
    try {
        const url = new URL(redirectUrl, window.location.origin);
        onSuccess();
        window.location.assign(url);
    } catch (error) {
        onError(
            error instanceof Error
                ? error.message
                : `Invalid URL: ${redirectUrl}`
        );
    }
};
